import { useState } from 'react'

import { cn } from '@/lib/cn'
import { initials } from '@/lib/format'

export interface AvatarProps {
  name: string
  /** A short-lived signed URL for the person's photo (FR-58, AD-18). Absent for
   *  everyone who has not set one, which is the ordinary case — the initials
   *  below are the design, not a placeholder for a missing image. */
  src?: string
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const SIZES = {
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-[11.5px]',
  lg: 'size-10 text-[13px]',
} as const

/* Base UI (`@base-ui/react`): `Avatar` considered and rejected. `Avatar.Image` gates rendering on
   `useImageLoadingStatus`, which constructs `new window.Image()` and waits for
   `onload`; jsdom never fires it, so the <img> would never render under test
   and Avatar.test.tsx asserts it does. */
export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  // A signed photo URL lives five minutes (AD-18), so an avatar on a page left
  // open over lunch will eventually 403. Falling back to initials on error is
  // what keeps that from rendering as a broken-image icon beside somebody's
  // name — the fallback is already the design for most of the company.
  //
  // The failed URL is stored rather than a boolean, so a re-minted link clears
  // the failure by simply not matching it. A boolean would need an effect to
  // reset it, which is a cascading render for something derivable.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const failed = src !== undefined && src === failedSrc

  return (
    <span
      title={name}
      className={cn(
        'bg-neutral-100 text-text-muted inline-flex shrink-0 items-center justify-center',
        'rounded-full font-medium select-none',
        // overflow-hidden so a non-square photo is clipped to the circle rather
        // than escaping it.
        'overflow-hidden',
        SIZES[size],
        className,
      )}
    >
      {src && !failed ? (
        <img
          src={src}
          // Empty alt with the name on the title above: the photo is decorative
          // in every place it appears — the person's name is always rendered
          // beside it — so announcing it twice is noise to a screen reader.
          alt=""
          className="size-full object-cover"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        initials(name)
      )}
    </span>
  )
}
